import { useState } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { sendgridApi } from '../api/sendgrid';
import SlideOverPanel from '../components/SlideOverPanel';
import {
  BoltIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { SENDGRID_LIST_NAMES } from '../constants/sendgrid';

const EVENT_TYPE_COLORS = {
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  open: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  click: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  group_resubscribe: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  deferred: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  processed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  unsubscribe: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  group_unsubscribe: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  bounce: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  spam_report: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  dropped: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const EVENT_TYPES = [
  'processed',
  'dropped',
  'delivered',
  'deferred',
  'bounce',
  'open',
  'click',
  'spam_report',
  'unsubscribe',
  'group_unsubscribe',
  'group_resubscribe',
];

function EventTypeBadge({ type }) {
  const colorClass = EVENT_TYPE_COLORS[type] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {type}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

function formatTimestamp(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${formatRelativeDate(dateStr)} (${time})`;
}

function truncateUrl(url, maxLen = 60) {
  if (!url) return null;
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '...';
}

function SkeletonRows({ count = 5 }) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={i}>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-40" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32" /></td>
      <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" /></td>
    </tr>
  ));
}

function EventsTable({ events, onEmailClick }) {
  if (!events || events.length === 0) {
    return (
      <tr>
        <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No events found.
        </td>
      </tr>
    );
  }

  return events.map((event, idx) => (
    <tr key={event.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
      <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
        {formatTimestamp(event.timestamp)}
      </td>
      <td className="px-4 py-2.5 text-sm font-medium">
        <button
          onClick={() => onEmailClick(event.email)}
          className="text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        >
          {event.email}
        </button>
      </td>
      <td className="px-4 py-2.5">
        <EventTypeBadge type={event.eventType} />
      </td>
      <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate" title={event.campaignName || event.subject || ''}>
        {event.campaignName || event.subject || '-'}
      </td>
      <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
        {event.url ? (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 hover:underline truncate block"
            title={event.url}
          >
            {truncateUrl(event.url)}
          </a>
        ) : (event.reason || event.response || event.status || '-')}
      </td>
    </tr>
  ));
}

function EventsList({ events }) {
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      {events.map((event, idx) => {
        const detail = event.reason || event.response || event.status || null;
        return (
          <div key={event.id || idx} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap w-28 shrink-0">
                {formatRelativeDate(event.timestamp)}
              </span>
              <EventTypeBadge type={event.eventType} />
              {event.campaignName && (
                <span className="text-sm text-gray-700 dark:text-gray-200 truncate font-medium">{event.campaignName}</span>
              )}
              {!event.campaignName && event.subject && (
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{event.subject}</span>
              )}
            </div>
            {(event.url || detail) && (
              <div className="ml-[7.75rem] mt-1 flex flex-col gap-0.5">
                {event.url && (
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 hover:underline truncate"
                    title={event.url}
                  >
                    {truncateUrl(event.url, 80)}
                  </a>
                )}
                {detail && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{detail}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ContactDetailPanel({ email, contact, isLoading }) {
  // Fetch recent events for this contact
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['localContactEvents', email],
    queryFn: () => sendgridApi.getLocalContactEvents(email, { limit: 25 }),
    enabled: !!email,
  });

  const recentEvents = eventsData?.events || [];

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200 text-sm">No local contact record found for {email}</p>
        </div>

        {/* Still show events even without a contact record */}
        {recentEvents.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Recent Events</h3>
            <EventsList events={recentEvents} />
          </div>
        )}
      </div>
    );
  }

  const eng = contact.engagement || {};
  const sup = contact.suppression || {};

  return (
    <div className="p-6 space-y-4">
      {/* Contact Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Contact Info</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Email</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
            <p className="text-sm text-gray-900 dark:text-gray-100 capitalize">{contact.status || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">First Name</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.firstName || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Name</label>
            <p className="text-sm text-gray-900 dark:text-gray-100">{contact.lastName || '-'}</p>
          </div>
        </div>

        {/* Lists */}
        {contact.listIds?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Lists</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {contact.listIds.map((lid) => (
                <span
                  key={lid}
                  className={`inline-block px-2 py-0.5 text-xs rounded ${
                    SENDGRID_LIST_NAMES[lid] ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {SENDGRID_LIST_NAMES[lid] || lid}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Engagement Summary */}
      {(eng.totalDelivered > 0 || eng.totalOpens > 0 || eng.totalClicks > 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Engagement</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{eng.totalDelivered || 0}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{eng.totalOpens || 0}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Opens</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{eng.totalClicks || 0}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Clicks</div>
            </div>
          </div>
        </div>
      )}

      {/* Suppression info */}
      {(sup.bounced || sup.spamReported || sup.unsubscribed) && (
        <div className="bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800/50 p-4">
          <h2 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Suppressions</h2>
          <div className="flex gap-4 text-sm text-red-700 dark:text-red-300">
            {sup.bounced && <span>Bounced: {formatDate(sup.bouncedAt)}</span>}
            {sup.spamReported && <span>Spam: {formatDate(sup.spamReportedAt)}</span>}
            {sup.unsubscribed && <span>Unsub: {formatDate(sup.unsubscribedAt)}</span>}
          </div>
        </div>
      )}

      {/* Recent Events */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Recent Events</h2>
        {eventsLoading ? (
          <div className="text-sm text-gray-400 dark:text-gray-500">Loading events...</div>
        ) : recentEvents.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No events found</div>
        ) : (
          <EventsList events={recentEvents} />
        )}
      </div>
    </div>
  );
}

function EventsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [mode, setMode] = useState('by-date'); // 'by-date' | 'by-type' | 'by-message'
  const [eventType, setEventType] = useState('delivered');
  const [messageIdInput, setMessageIdInput] = useState('');
  const [messageIdQuery, setMessageIdQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);

  // By Date — infinite query
  const byDateQuery = useInfiniteQuery({
    queryKey: ['events', 'by-date', date],
    queryFn: ({ pageParam }) => sendgridApi.listEventsByDate(date, { limit: 50, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
    initialPageParam: undefined,
    enabled: mode === 'by-date',
  });

  // By Type — infinite query
  const byTypeQuery = useInfiniteQuery({
    queryKey: ['events', 'by-type', date, eventType],
    queryFn: ({ pageParam }) => sendgridApi.listEventsByType(date, eventType, { limit: 50, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
    initialPageParam: undefined,
    enabled: mode === 'by-type',
  });

  // By Message ID — regular query
  const byMessageQuery = useQuery({
    queryKey: ['events', 'by-message', messageIdQuery],
    queryFn: () => sendgridApi.getEventsByMessageId(messageIdQuery),
    enabled: mode === 'by-message' && !!messageIdQuery,
  });

  // Fetch local contact for slide-over
  const { data: contactData, isLoading: contactLoading } = useQuery({
    queryKey: ['localContact', selectedEmail],
    queryFn: () => sendgridApi.getLocalContact(selectedEmail),
    enabled: !!selectedEmail,
  });

  const handleMessageIdSearch = (e) => {
    e.preventDefault();
    if (messageIdInput.trim()) {
      setMessageIdQuery(messageIdInput.trim());
    }
  };

  // Determine current data/state
  let events = [];
  let isLoading = false;
  let error = null;
  let hasNextPage = false;
  let isFetchingNextPage = false;
  let fetchNextPage = null;

  if (mode === 'by-date') {
    events = byDateQuery.data?.pages.flatMap((p) => p.events) || [];
    isLoading = byDateQuery.isLoading;
    error = byDateQuery.error;
    hasNextPage = byDateQuery.hasNextPage;
    isFetchingNextPage = byDateQuery.isFetchingNextPage;
    fetchNextPage = byDateQuery.fetchNextPage;
  } else if (mode === 'by-type') {
    events = byTypeQuery.data?.pages.flatMap((p) => p.events) || [];
    isLoading = byTypeQuery.isLoading;
    error = byTypeQuery.error;
    hasNextPage = byTypeQuery.hasNextPage;
    isFetchingNextPage = byTypeQuery.isFetchingNextPage;
    fetchNextPage = byTypeQuery.fetchNextPage;
  } else if (mode === 'by-message') {
    events = byMessageQuery.data?.events || [];
    isLoading = byMessageQuery.isLoading;
    error = byMessageQuery.error;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <BoltIcon className="h-7 w-7 text-gray-600 dark:text-gray-300" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Events</h1>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        {/* Mode selector */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">View</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
          >
            <option value="by-date">By Date</option>
            <option value="by-type">By Type</option>
            <option value="by-message">By Message ID</option>
          </select>
        </div>

        {/* Date picker — shown for by-date and by-type modes */}
        {mode !== 'by-message' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            />
          </div>
        )}

        {/* Event type dropdown — by-type mode only */}
        {mode === 'by-type' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Event Type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        {/* Message ID search — by-message mode only */}
        {mode === 'by-message' && (
          <form onSubmit={handleMessageIdSearch} className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Message ID</label>
              <input
                type="text"
                value={messageIdInput}
                onChange={(e) => setMessageIdInput(e.target.value)}
                placeholder="Enter SendGrid message ID..."
                className="block text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 rounded-lg px-3 py-1.5 w-80 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
              />
            </div>
            <button
              type="submit"
              disabled={!messageIdInput.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-obn-blue rounded-lg hover:bg-obn-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </button>
          </form>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4 mb-4">
          <p className="text-red-800 dark:text-red-200 text-sm">Error loading events: {error.message}</p>
        </div>
      )}

      {/* Events Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detail</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <SkeletonRows />
              ) : (
                <EventsTable events={events} onEmailClick={setSelectedEmail} />
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasNextPage && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {isFetchingNextPage ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}

      {/* Result count */}
      {!isLoading && events.length > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
          {events.length} event{events.length !== 1 ? 's' : ''} loaded
        </p>
      )}

      {/* Contact detail slide-over */}
      <SlideOverPanel
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        title="Contact Details"
        width="50vw"
      >
        {selectedEmail && (
          <ContactDetailPanel
            email={selectedEmail}
            contact={contactData}
            isLoading={contactLoading}
          />
        )}
      </SlideOverPanel>
    </div>
  );
}

export default EventsPage;
